import { ApiError, apiErrorReason } from '@/lib/api';
import { daysBetweenDays, formatDateTime } from '@/lib/time';
import { dispositionFileName as pdfFileName } from '@/lib/download';

import type { Legend, PrintEventType, PrintLogFilters, Purpose } from './types';
import {
  isTypeRestricted,
  NO_OFFICIAL_VERSION_TEXT,
  NOT_CURRENT_TEXT,
  restrictedActionText,
} from '@/lib/document-capabilities';
import { errorAdvice } from '@/lib/error-advice';

// Textos y reglas de presentación de la pantalla Marca de agua e impresión (so=400). Puro.

export interface Badge {
  cls: 'b-info' | 'b-mut';
  label: string;
}

export const LEGEND_MAX = 120;
export const PRINT_LOG_PAGE_SIZE = 25;
const SAMPLE_FOLIO = '7KQ2M9XA4T';

export function eventBadge(type: PrintEventType): Badge {
  return type === 'print_view'
    ? { cls: 'b-info', label: 'Vista de impresión' }
    : { cls: 'b-mut', label: 'Descarga PDF' };
}

const PURPOSE: Record<string, string> = { interna: 'Interna', externa: 'Externa' };

export function purposeLabel(purpose: string): string {
  return PURPOSE[purpose] ?? purpose;
}

export function originLabel(ip: string | null): string {
  return ip ? `Web · ${ip}` : 'Web';
}

export function versionLabel(version: number | null, revisionLabel: string | null): string {
  if (version !== null) {
    return `v${version}`;
  }
  return revisionLabel ? `v${revisionLabel}` : '—';
}

export function legendText(legends: Legend[], purpose: Purpose): string {
  return legends.find((l) => l.key === purpose)?.text ?? '';
}

/** Misma regla que el backend: 1-120 caracteres, solo Latin-1 y sin caracteres de control. */
export function legendError(text: string): string | null {
  const t = text.trim();
  if (t === '') {
    return 'La leyenda no puede quedar vacía.';
  }
  if (t.length > LEGEND_MAX) {
    return `Máximo ${LEGEND_MAX} caracteres.`;
  }
  const latin1 = [...t].every((ch) => (ch.codePointAt(0) ?? 0) <= 0xff);
  if (!latin1 || /\p{Cc}/u.test(t)) {
    return 'Solo letras, números y signos del español (sin emojis).';
  }
  return null;
}

/** Línea de trazabilidad de ejemplo, con el formato que estampa el sistema. */
export function sampleTraceLine(company: string, user: string, at: Date): string {
  const when = formatDateTime(at.toISOString());
  return `${company.toUpperCase()} · ${user.toUpperCase()} · ${when} · FOLIO ${SAMPLE_FOLIO}`;
}

/** Respaldo del nombre del PDF, con la misma regla del servidor, si falta su header. */
export function copyFileName(code: string, purpose: Purpose): string {
  return `${code.replace(/[^A-Za-z0-9._-]/g, '_')}-${purpose}.pdf`;
}

/** Nombre del PDF que manda el servidor en Content-Disposition (ver lib/download). */
export function dispositionFileName(header: string | null): string | null {
  return pdfFileName(header, 'pdf');
}

/** Filtros de la bitácora presentes, como parámetros del endpoint. */
function printLogParams(f: PrintLogFilters): URLSearchParams {
  const q = new URLSearchParams();
  const pairs: [string, string | null][] = [
    ['entryId', f.doc],
    ['type', f.tipo],
    ['purpose', f.proposito],
    ['from', f.desde],
    ['to', f.hasta],
    ['folio', f.folio],
    ['userId', f.persona],
  ];
  for (const [key, value] of pairs) {
    if (value) {
      q.set(key, value);
    }
  }
  return q;
}

/** Query de la bitácora de la empresa con los filtros presentes. */
export function printLogQuery(f: PrintLogFilters, cursor: string | null): string {
  const q = printLogParams(f);
  if (cursor) {
    q.set('cursor', cursor);
  }
  q.set('limit', String(PRINT_LOG_PAGE_SIZE));
  return q.toString();
}

/** Filtros para exportar (spec 2026-10-14 §3): los de la vista, sin cursor ni página. */
export function printLogExportQuery(f: PrintLogFilters): string {
  return printLogParams(f).toString();
}

const MAX_RANGE_DAYS = 366;

/** Mismo límite que el backend para el rango de la bitácora; null si es válido. */
export function dateRangeError(desde: string | null, hasta: string | null): string | null {
  if (desde === null || hasta === null) {
    return null;
  }
  if (desde > hasta) {
    return 'La fecha "Desde" no puede ser posterior a "Hasta".';
  }
  const days = daysBetweenDays(desde, hasta);
  return days < MAX_RANGE_DAYS ? null : 'El rango de fechas no puede superar 366 días.';
}

/** Error al cargar la bitácora: un 400 es un filtro inválido; lo demás, según su causa. */
export function printLogErrorCopy(err: unknown): string {
  return err instanceof ApiError && err.status === 400
    ? 'Algún filtro no es válido. Revísalo o limpia los filtros.'
    : errorAdvice(err);
}

export function copyErrorCopy(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 409 && apiErrorReason(err) === 'document_not_current') {
      return NOT_CURRENT_TEXT;
    }
    if (err.status === 422 && apiErrorReason(err) === 'legend_unavailable') {
      return 'La leyenda de ese tipo de copia está desactivada.';
    }
    if (err.status === 422 && apiErrorReason(err) === 'no_binary') {
      return 'El documento no tiene un archivo asociado.';
    }
    if (err.status === 422 && apiErrorReason(err) === 'no_official_version') {
      return NO_OFFICIAL_VERSION_TEXT;
    }
    if (err.status === 422 && apiErrorReason(err) === 'no_viewable_file') {
      return 'La versión vigente no tiene un archivo que se pueda copiar. Avisa al Administrador de Calidad.';
    }
    if (err.status === 403 && isTypeRestricted(err)) {
      return restrictedActionText('imprimir');
    }
    if (err.status === 403) {
      return 'No tienes permiso para imprimir documentos.';
    }
  }
  return `No se pudo generar la copia. ${errorAdvice(err)}`;
}

export function settingsErrorCopy(err: unknown): string {
  if (err instanceof ApiError && err.status === 403) {
    return 'Ya no tienes permiso para editar la marca de agua.';
  }
  if (err instanceof ApiError && err.status === 400) {
    return 'Revisa los valores: alguno está fuera del rango permitido.';
  }
  return `No se pudo guardar. ${errorAdvice(err)}`;
}
